import { put, takeEvery, takeLatest } from "redux-saga/effects";
import { Action } from "redux";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import apiErrorHandler from "@/utils/apiErrorHandler";
import {
  GET_SCORECARD_API_HTTP_STATUS_CODES,
  GET_SCORECARD_ERROR_MESSAGE,
  UPDATE_SCORECARD_ERROR_MESSAGE,
} from "@/constants/InterviewerConstants";
import { showToast } from "../actions/appStatus.action";
import {
  createInterviewRatingsAPI,
  getScorecardAPI,
  updateSingleRatingAPI,
  updateStatusAPI,
  submitQuestionScoreAPI,
  updateQuestionScoreAPI,
} from "../api/interviewer.api";
import {
  UPDATE_INTERVIEWER_DETAILS,
  UPDATE_INTERVIEW_RATING,
  GET_SCORECARD,
  CREATE_INTERVIEW_RATING,
  SUBMIT_QUESTION_SCORE,
  UPDATE_QUESTION_SCORE,
} from "../constants/ActionTypes";
import {
  ERatingType,
  IUpdateInterviewerAction,
  IUpdateRatingArgs,
} from "../types/interview.types";
import {
  IInterviewerRating,
  IQuestionScoreAction,
  ISubmitQuestionScorePayload,
  IUpdateSingleRatingPayload,
} from "../types/interviewer.types";
import { goToDoSelectFeedbackRoute } from "../utils/switchRoute.utils";
import { call } from "./utils.saga";

interface IScoreCardPayload extends Action {
  payload: {
    interviewSlug: string;
  };
}

function* getScoreCard({ payload }: IScoreCardPayload) {
  try {
    const { interviewSlug } = payload;
    const resp = yield* call(getScorecardAPI, interviewSlug);

    yield put({
      type: `${GET_SCORECARD}_SUCCESS`,
      rating: resp.data,
    });
  } catch (error) {
    const message = yield* call(
      apiErrorHandler,
      error,
      GET_SCORECARD_API_HTTP_STATUS_CODES,
      GET_SCORECARD_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* updateRating({ payload }: IUpdateRatingArgs) {
  try {
    const {
      interviewSlug,
      feedbackSlug,
      criteria,
      name,
      ratingType,
      category,
      ratingValue,
      description,
      status,
      ratingSubTypes,
      order,
      source,
    } = payload;
    let body: IUpdateSingleRatingPayload = {
      source,
    };
    if (status) {
      body = {
        status,
      };
    } else if (criteria && ratingType && name) {
      let value = {};

      if (ratingType === ERatingType.RATING) {
        value = { rating: ratingValue };
      }

      if (ratingType === ERatingType.DESCRIPTION) {
        value = { description };
      }

      if (ratingType === ERatingType.HYBRID) {
        if (
          ratingSubTypes?.includes("rating") &&
          ratingSubTypes?.includes("description")
        ) {
          value = { rating: ratingValue, description };
        }
      }
      body = {
        evaluation_ratings: {
          [criteria]: {
            name,
            type: ratingType,
            category,
            sub_types: ratingSubTypes,
            order,
            ...value,
          },
        },
        source,
      };
    }
    const resp = yield* call(
      feedbackSlug ? updateSingleRatingAPI : createInterviewRatingsAPI,
      interviewSlug,
      feedbackSlug,
      body,
    );
    yield put({
      type: `${
        feedbackSlug ? UPDATE_INTERVIEW_RATING : CREATE_INTERVIEW_RATING
      }_SUCCESS`,
      rating: resp.data,
    });
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      GET_SCORECARD_API_HTTP_STATUS_CODES,
      UPDATE_SCORECARD_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* updateInterviewer({ payload }: IUpdateInterviewerAction) {
  try {
    const {
      slug,
      status,
      interviewSlug,
      feedbackSlug,
      evaluationRatings,
      navigate,
      source,
    } = payload;
    const body: IInterviewerRating = {
      status,
      slug,
      evaluation_ratings: evaluationRatings,
      source,
    };
    const resp = yield* call(
      feedbackSlug ? updateStatusAPI : createInterviewRatingsAPI,
      interviewSlug,
      feedbackSlug,
      body,
    );
    yield put({
      type: `${UPDATE_INTERVIEWER_DETAILS}_SUCCESS`,
      rating: resp.data,
    });
    if (navigate) goToDoSelectFeedbackRoute(navigate, interviewSlug);
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      GET_SCORECARD_API_HTTP_STATUS_CODES,
      UPDATE_SCORECARD_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* executeQuestionScore(
  payload: ISubmitQuestionScorePayload,
  apiCall: typeof submitQuestionScoreAPI,
  successType: string,
) {
  try {
    const { interviewSlug, questionSlug, score, description } = payload;
    const resp = yield* call(apiCall, interviewSlug, questionSlug, {
      score,
      description,
    });
    yield put({
      type: successType,
      questionSlug,
      ratingData: resp.data.data.rating_data,
    });
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      GET_SCORECARD_API_HTTP_STATUS_CODES,
      UPDATE_SCORECARD_ERROR_MESSAGE,
    );
    if (message) yield put(showToast({ message, type: EToastType.ERROR }));
  }
}

function* submitQuestionScore({ payload }: IQuestionScoreAction) {
  yield* executeQuestionScore(
    payload,
    submitQuestionScoreAPI,
    `${SUBMIT_QUESTION_SCORE}_SUCCESS`,
  );
}

function* updateQuestionScore({ payload }: IQuestionScoreAction) {
  yield* executeQuestionScore(
    payload,
    updateQuestionScoreAPI,
    `${UPDATE_QUESTION_SCORE}_SUCCESS`,
  );
}

export default function* loadInterviewerSaga() {
  yield takeLatest(GET_SCORECARD, getScoreCard);
  yield takeEvery(CREATE_INTERVIEW_RATING, updateRating);
  yield takeEvery(UPDATE_INTERVIEW_RATING, updateRating);
  yield takeEvery(UPDATE_INTERVIEWER_DETAILS, updateInterviewer);
  yield takeEvery(SUBMIT_QUESTION_SCORE, submitQuestionScore);
  yield takeEvery(UPDATE_QUESTION_SCORE, updateQuestionScore);
}
